import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, IconButton, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Rocket, Trash2 } from 'lucide-react';

import StatusChip from '../../../../features/area/components/RulesCard/StatusChip/StatusChip';

import {
  BoostDuration,
  BoostStatus,
  deleteButtonSx,
  EMPTY_ICON_SIZE,
  getBadgeImgSx,
  getCardSx,
  getEmptyIconBoxSx,
  getEmptyTitleTextSx,
  getTimerLabelTextSx,
  getTimerValueTextSx,
  getTitleTextSx,
  leftStackProps,
  queuedStatusChipSx,
  rightStackProps,
  timerStackProps,
  topRowSx,
  chipDesktopSx,
  chipMobileSx,
} from './styles';

import MARGIN_BOOST_2H_BADGE from '../../../../assets/images/store/MARGIN_BOOST_2H_BADGE.png';
import MARGIN_BOOST_4H_BADGE from '../../../../assets/images/store/MARGIN_BOOST_4H_BADGE.png';
import MARGIN_BOOST_8H_BADGE from '../../../../assets/images/store/MARGIN_BOOST_8H_BADGE.png';
import MARGIN_BOOST_12H_BADGE from '../../../../assets/images/store/MARGIN_BOOST_12H_BADGE.png';
import MARGIN_BOOST_24H_BADGE from '../../../../assets/images/store/MARGIN_BOOST_24H_BADGE.png';

const badgeByDuration: Record<BoostDuration, string> = {
  2: MARGIN_BOOST_2H_BADGE,
  4: MARGIN_BOOST_4H_BADGE,
  8: MARGIN_BOOST_8H_BADGE,
  12: MARGIN_BOOST_12H_BADGE,
  24: MARGIN_BOOST_24H_BADGE,
};

const titleByDuration: Record<BoostDuration, string> = {
  2: 'BOOST_CARD.TITLE_2H',
  4: 'BOOST_CARD.TITLE_4H',
  8: 'BOOST_CARD.TITLE_8H',
  12: 'BOOST_CARD.TITLE_12H',
  24: 'BOOST_CARD.TITLE_24H',
};

const EMPTY_TITLE = 'BOOST_CARD.EMPTY_TITLE';
const EMPTY_QUEUED_TITLE = 'BOOST_CARD.EMPTY_QUEUED_TITLE';

const formatCountdown = (remainingMs: number): string => {
  if (remainingMs <= 0) return '0:00:00';
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

interface BoostCardProps {
  status: BoostStatus;
  duration?: BoostDuration;
  queued?: boolean;
  expiresAt?: number | null;
  onDelete?: () => void;
  onExpire?: () => void;
}

const BoostCard: React.FC<BoostCardProps> = ({
  status,
  duration,
  queued,
  expiresAt,
  onDelete,
  onExpire,
}) => {
  const { t } = useTranslation('BoostModal');

  const timerLabel = queued
    ? t('BOOST_CARD.TIMER_LABEL_QUEUED')
    : t('BOOST_CARD.TIMER_LABEL_ACTIVE');

  const [countdown, setCountdown] = useState<string>('');
  const [hasExpired, setHasExpired] = useState(false);

  useEffect(() => {
    setHasExpired(false);
  }, [expiresAt]);

  useEffect(() => {
    if (status === 'empty' || !expiresAt) return;

    const tick = () => {
      const remaining = expiresAt - Date.now();
      setCountdown(formatCountdown(remaining));

      if (remaining <= 0 && !hasExpired && onExpire) {
        setHasExpired(true);
        onExpire();
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [status, expiresAt, hasExpired, onExpire]);

  const getStatusChipStatus = () => {
    if (status === 'active') return 'Active';
    if (status === 'waiting') return 'Waiting for launch';
    return 'Empty';
  };

  const chipSx = queued ? queuedStatusChipSx : undefined;

  if (status === 'empty') {
    const iconSize = queued ? EMPTY_ICON_SIZE.queued : EMPTY_ICON_SIZE.normal;
    const emptyTitle = queued ? EMPTY_QUEUED_TITLE : EMPTY_TITLE;

    return (
      <Card sx={getCardSx(queued)}>
        <Box sx={topRowSx}>
          <Stack {...leftStackProps}>
          <Box sx={getEmptyIconBoxSx(queued)}>
            <Rocket size={iconSize} color="#CCD4FF" />
          </Box>
          <Typography sx={getEmptyTitleTextSx(queued)}>
            {t(emptyTitle)}
          </Typography>
        </Stack>

          <StatusChip
            small={queued}
            status="Empty"
            sx={[chipMobileSx, ...(chipSx ? [chipSx] : [])] as SxProps<Theme>}
          />
        </Box>

        <Stack {...rightStackProps}>
          <StatusChip
            small={queued}
            status="Empty"
            sx={[chipDesktopSx, ...(chipSx ? [chipSx] : [])] as SxProps<Theme>}
          />
        </Stack>
      </Card>
    );
  }

  return (
    <Card sx={getCardSx(queued)}>
      <Box sx={topRowSx}>
        <Stack {...leftStackProps}>
          <Box
            component="img"
            src={badgeByDuration[duration!]}
            sx={getBadgeImgSx(queued)}
          />
          <Typography sx={getTitleTextSx(queued)}>
            {t(titleByDuration[duration!])}
          </Typography>
        </Stack>

        <StatusChip
          small={queued}
          status={getStatusChipStatus()}
          sx={[chipMobileSx, ...(chipSx ? [chipSx] : [])] as SxProps<Theme>}
        />
      </Box>

      <Stack {...rightStackProps}>
        <Stack {...timerStackProps}>
          <Typography sx={getTimerLabelTextSx(queued)}>{timerLabel}</Typography>
          <Typography sx={getTimerValueTextSx(queued)}>{countdown}</Typography>
        </Stack>

        <StatusChip
          small={queued}
          status={getStatusChipStatus()}
          sx={[chipDesktopSx, ...(chipSx ? [chipSx] : [])] as SxProps<Theme>}
        />

        {queued && (
          <IconButton sx={deleteButtonSx} onClick={onDelete}>
            <Trash2 size={18} />
          </IconButton>
        )}
      </Stack>
    </Card>
  );
};

export default BoostCard;
